/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { loadStripe } from '@stripe/stripe-js';
import { checkoutHandler } from './checkoutHandler';
import {
  getRequest,
  localizedPrice,
  localizedPriceFromMinorUnit,
  majorUnitAmount,
  minorUnitAmount,
} from '../util';
import { OfferStatus } from '../constants';
import { useToast } from './useToast';
import { trackEvent } from '../googleAnalytics';

declare global {
  interface Window {
    Kajabi: any;
  }
}

// TODO: This file was originally created in JS, and was converted to TS later
// We ought to use types rather than `any` where possible
export default function usePopupCheckout(offerToken: string, enableModal: boolean) {
  const { setValue, reset, watch, setError, getValues } = useFormContext();
  const { t } = useTranslation();
  const [data, setData] = useState(null) as any;
  const [modalActive, setModalActive] = useState(false);
  const [offerDataLoaded, setOfferDataLoaded] = useState(false);
  // @ts-ignore -- TODO: add right type here
  const { showToast } = useToast();
  const isPaymentInProgress = watch('isPaymentInProgress');
  const priceOverrideRequired = watch('priceOverrideRequired');
  const priceOverride = watch('priceOverride');
  const useSavedCard = watch('useSavedCard');
  const coupon = watch('coupon');
  const orderBumpActive = watch('orderBumpActive');

  const siteUserSignedIn = window.Kajabi?.currentSiteUser?.type === 'User';

  const handleHttpError = useCallback((err) => {
    const status = err.response?.status;
    setValue('isPaymentInProgress', false);
    if (status === 404) {
      showToast({ title: t('messages.offer_not_found'), type: 'danger' });
    } else if (status === 401) {
      showToast({ title: t('messages.token_expiry'), type: 'danger' });
    } else {
      showToast({ title: t('messages.something_went_wrong'), type: 'danger' });
    }
    handleHideModal();
  }, []);

  useEffect(() => {
    if (!modalActive) return;
    getRequest(`/api/offers/${offerToken}/checkout`)
      .then((response) => {
        const responseData = response.data.data;
        if (response.status === 200) {
          setValuesFromResponse(responseData);
          trackEvent('view_cart', responseData);
        }
      })
      .catch((err) => handleHttpError(err));
  }, [modalActive]);

  const setValuesFromResponse = (responseData: any) => {
    const { offer } = responseData;
    setData(responseData);
    setValue('offer', offer);
    setValue('site', responseData.site);
    setValue('price', responseData.price);
    if (responseData.orderBump) {
      setValue('orderBump', responseData.orderBump);
    }

    setValue('taxable', responseData.offer.taxable);
    setValue('taxInclusive', responseData.offer.taxInclusive);
    setValue('nameRequired', responseData.offer.nameRequired);
    setValue('phoneRequired', responseData.offer.phoneNumberRequired);
    setValue('addressRequired', responseData.offer.addressRequired);
    setValue('collectTaxId', responseData.offer.collectTaxId);
    setValue('countriesToValidateTaxId', responseData.offer.countriesToValidateTaxId);
    setValue('address.subdivisionRequired', false);
    if (responseData.offer.customFields) {
      setValue('customFieldData', responseData.offer.customFields); // customFields = []
    }
    setValue('priceOverrideRequired', responseData.price.priceStrategy === 'pay_what_you_want');
    setValue(
      'priceOverride',
      majorUnitAmount(responseData.price.amount, responseData.price.exponent),
    );
    setValue('offerPurchase', responseData.offerPurchase);
    if (responseData.offer.additionalPaymentMethods) {
      const additionalPaymentMethods = responseData.offer.additionalPaymentMethods.map(
        (method: any) => {
          if (method === 'afterpay_clearpay') return 'afterpay_clearpay';
          return method;
        },
      );
      setValue('additionalPaymentMethods', additionalPaymentMethods);
    }
    setValue('activeFeatures', responseData.features); // activeFeatures = []
    if (responseData.member) setValue('member', responseData.member);
    setValue('useSavedCard', !!responseData.member?.savedCardDetails);
    setValue('saveCard', responseData.site.cardStorageOptinPreference === 'opt_out');
    if (responseData.showOptIn) {
      setValue('showOptIn', true);
      setValue('optInDefaultValue', responseData.optInDefaultValue);
      setValue('optInText', responseData.optInTextOverride);
    }
    const isPaypalOffer =
      offer.paymentMethods?.length === 1 && offer.paymentMethods?.includes('paypal');
    if (isPaypalOffer) {
      setValue('paymentProvider', 'external_paypal');
    }
    // Initialize Stripe here if needed and then set offer data as loaded
    // else just set offer data as loaded
    if (offer.stripePublishableKey) {
      const { stripePublishableKey, stripeAccountId } = offer;
      setStripe(stripePublishableKey, stripeAccountId).then(() => setOfferDataLoaded(true));
    } else {
      setOfferDataLoaded(true);
    }
  };

  const setStripe = async (stripeKey: string, stripeAccountId: any) => {
    const stripe = await loadStripe(stripeKey, {
      betas: ['elements_enable_deferred_intent_beta_1'],
      stripeAccount: stripeAccountId,
    });
    setValue('stripe', stripe);
  };

  const handleShowModal = useCallback(() => {
    setModalActive(enableModal);
  }, [enableModal]);

  const handleHideModal = useCallback(() => {
    if (!isPaymentInProgress) {
      trackEvent('abandoned_checkout', data);
      setModalActive(false);
      setOfferDataLoaded(false);
      reset({});
    }
  }, [reset, isPaymentInProgress, data]);

  const paymentProvider = watch('paymentProvider');
  const showSaveCardCheckbox = useMemo(() => {
    if (!data) return false;
    return (
      !useSavedCard &&
      data.price.priceType !== 'recurring' &&
      ['opt_in', 'opt_out'].includes(data.site.cardStorageOptinPreference) &&
      paymentProvider !== 'external_paypal'
    );
  }, [data, paymentProvider, useSavedCard]);

  const saveCardDefaultValue = useMemo(() => {
    if (!data || !showSaveCardCheckbox) return false;
    return data.site.cardStorageOptinPreference === 'opt_in';
  }, [data]);

  // Update pop up state when the coupon is applied / cleared
  useEffect(() => {
    if (!data) return;
    const { price } = data;
    // Pay what you want is not required when a coupon is applied and a coupon change will
    // reset the pay what you want amount
    if (coupon) {
      setValue('priceOverrideRequired', false);
    } else {
      setValue('priceOverrideRequired', price.priceStrategy === 'pay_what_you_want');
    }
    setValue('priceOverride', majorUnitAmount(price.amount, price.exponent), {
      shouldValidate: true,
    });

    // An order bump will be deactivated when a gift coupon is applied.
    if (coupon?.giftCoupon) {
      setValue('orderBumpActive', false);
    }
  }, [data, coupon]);

  const isOfferUnavailable = useMemo(() => {
    if (!data) return false;
    return data.offer.status !== OfferStatus.AVAILABLE;
  }, [data]);

  // Fetch tax as the address is changed
  const taxable = watch('taxable');
  const addressCountry = watch('address.country');
  const addressZip = watch('address.zip');
  const addressLine1 = watch('address.addressLine1');
  const addressCity = watch('address.city');
  const addressSubdivision = watch('address.subdivision');
  const currentTaxId = watch('taxId');

  useEffect(() => {
    if (!modalActive || !taxable) return;

    if (addressCountry || addressZip || addressLine1 || addressCity || addressSubdivision) {
      const timer = setTimeout(() => {
        const taxPayload = {
          country: addressCountry,
          zip: addressZip,
          line1: addressLine1,
          city: addressCity,
          subdivision: addressSubdivision,
          couponCode: coupon?.couponCode,
          taxId: currentTaxId,
        };
        getRequest(`/api/offers/${offerToken}/checkout/tax`, { params: taxPayload })
          .then((response) => {
            setValue('price.tax', response.data.data.tax);
          })
          .catch((err) => handleHttpError(err));
      }, 1000);
      return () => clearTimeout(timer); // eslint-disable-line consistent-return
    }
  }, [
    modalActive,
    taxable,
    addressCountry,
    addressZip,
    addressLine1,
    addressCity,
    addressSubdivision,
    currentTaxId,
    coupon,
  ]);

  const countriesToValidateTaxId = watch('countriesToValidateTaxId');

  useEffect(() => {
    if (!modalActive || !taxable || !countriesToValidateTaxId) return;

    if (countriesToValidateTaxId.includes(addressCountry) && currentTaxId) {
      const timer = setTimeout(() => {
        const payload = {
          country: addressCountry,
          taxId: currentTaxId,
        };
        getRequest(`/api/offers/${offerToken}/checkout/tax_id_status`, { params: payload })
          .then((response) => {
            setValue('taxIdValidationStatus', response.data.data.status);
          })
          .catch((err) => handleHttpError(err));
      }, 1000);
      return () => clearTimeout(timer); // eslint-disable-line consistent-return
    }
  }, [modalActive, taxable, countriesToValidateTaxId, addressCountry, currentTaxId]);

  const priceOverrideInCents = useMemo(() => {
    if (!data || priceOverride === '') return '';
    return minorUnitAmount(priceOverride, data.price.exponent);
  }, [data, priceOverride]);

  // This is the base offer price
  const displayPrice = useMemo(() => {
    if (!data) return '';
    // Prioritize the price text override when set
    if (data.offer.priceTextOverride) return data.offer.priceTextOverride;

    // Get the base price
    const { price } = data;
    const priceAmount = price.subscriptionPlanAttributes
      ? price.subscriptionPlanAttributes.paymentAmount
      : price.amount;

    // Special text when base price is free (but not a PWYW)
    if (priceAmount === 0 && !priceOverrideRequired) return t('form.free');

    // Format the base price
    const formattedPrice = localizedPriceFromMinorUnit(price.currency, priceAmount, price.exponent);
    return priceOverrideRequired ? `${formattedPrice} (${t('form.or_more')})` : formattedPrice;
  }, [data, priceOverrideRequired, t]);

  // This is the PWYW price
  const displayOverridePrice = useMemo(() => {
    if (!data) return null;
    if (!priceOverrideRequired) return null;
    const { price } = data;
    return localizedPrice(price.currency, priceOverride);
  }, [data, priceOverride]);

  const taxInclusive = watch('taxInclusive');
  const taxData = watch('price.tax');
  const dueNowPriceInCents = useMemo(() => {
    if (!data) return undefined;

    const { orderBump, price } = data;
    const orderBumpPrice = orderBumpActive ? orderBump?.price?.amount : 0;

    const taxPrice = taxData && !taxInclusive ? taxData.overallAmount : 0;
    const orderBumpTaxPrice = orderBumpActive && taxData ? taxData.orderBumpOverallAmount : 0;

    // The coupon API returns the due now amount so we can just use
    // that when a coupon is present.
    if (coupon) return coupon.dueNowAmount + orderBumpPrice;

    // For PWYW offers, return the price override
    if (priceOverrideRequired) return priceOverrideInCents + orderBumpPrice + taxPrice;

    // For one time return the price amount
    // For subscription, return the payment amount plus setup fee
    return price.amount + orderBumpPrice + taxPrice + orderBumpTaxPrice;
  }, [
    data,
    coupon,
    priceOverrideRequired,
    priceOverrideInCents,
    orderBumpActive,
    taxInclusive,
    taxData,
  ]);

  const isOfferFree = useMemo(() => {
    if (!data) return false;
    // A gift coupon makes the offer free
    if (coupon) return coupon.giftCoupon;

    // For a non-subscription offer, $0 due now means free.
    const { price } = data;
    if (!price.subscriptionPlanAttributes) return dueNowPriceInCents === 0;

    // For a subscription offer, $0 payments + $0 setup fee means free.
    const { paymentAmount, setupFee } = price.subscriptionPlanAttributes;
    return paymentAmount + setupFee === 0;
  }, [data, dueNowPriceInCents, coupon]);

  const redirectToFlow = (response: any) => {
    if (response.data.data.redirectUrl) {
      window.location.replace(response.data.data.redirectUrl);
    }
  };

  // Generate functions for form submission
  const { handleSubmit } = useFormContext();
  const submitForm = handleSubmit(() =>
    checkoutHandler(
      setValue,
      getValues,
      setError,
      isOfferFree,
      showToast,
      t,
      data,
      redirectToFlow,
      handleHttpError,
      showSaveCardCheckbox,
    ),
  );

  return {
    offerDataLoaded,
    modalActive,
    handleShowModal,
    handleHideModal,
    displayPrice,
    displayOverridePrice,
    dueNowPriceInCents,
    isOfferUnavailable,
    isOfferFree,
    submitForm,
    showSaveCardCheckbox,
    saveCardDefaultValue,
    siteUserSignedIn,
    priceOverrideInCents,
  };
}
